import { useEffect, useRef, useState } from 'react';
import {
	buildProductMedia3dMetricDetail,
	watchAndAttachProductMedia3dViewerListeners,
} from '../utils/product-media-3d-viewer-mount.js';

/**
 * @param {string} modelUrl
 * @param {{
 *   catalog?: string,
 *   skuStripped?: string,
 * }} [trackingContext]
 * @returns {{
 *   hasViewerLoadError: boolean,
 *   normalizedModelUrl: string,
 *   viewerContainerRef: import('react').RefObject<HTMLDivElement | null>,
 * }}
 */
export function useProductMedia3dViewerMount(modelUrl, trackingContext = {}) {
	const normalizedModelUrl = typeof modelUrl === 'string' ? modelUrl.trim() : '';
	const catalog = typeof trackingContext.catalog === 'string' ? trackingContext.catalog : undefined;
	const skuStripped = typeof trackingContext.skuStripped === 'string' ? trackingContext.skuStripped : undefined;
	const viewerContainerRef = useRef(null);
	const [hasViewerLoadError, setHasViewerLoadError] = useState(false);

	useEffect(() => {
		setHasViewerLoadError(false);
	}, [normalizedModelUrl]);

	useEffect(() => {
		if (normalizedModelUrl === '') {
			return undefined;
		}

		const containerElement = viewerContainerRef.current;
		if (containerElement === null) {
			return undefined;
		}

		const metricDetail = buildProductMedia3dMetricDetail(normalizedModelUrl, { catalog, skuStripped });
		let cancelled = false;

		const cleanup = watchAndAttachProductMedia3dViewerListeners(
			containerElement,
			metricDetail,
			() => cancelled,
			{ onViewerLoadFailed: () => setHasViewerLoadError(true) },
		);

		return () => {
			cancelled = true;
			cleanup();
		};
	}, [catalog, normalizedModelUrl, skuStripped]);

	return {
		hasViewerLoadError,
		normalizedModelUrl,
		viewerContainerRef,
	};
}
